import { Button } from "@/components/ui/Button";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import { useSessionContext } from "@/context/SessionContext";
import type { PublicWatchDogOffer } from "@/types";
import { ArrowUpCircleIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import { useEffect, useState } from "react";

type RankedEstateMembershipSlideProps = {
  offer: PublicWatchDogOffer;
};

export function RankedEstateMembershipSlide({
  offer,
}: RankedEstateMembershipSlideProps) {
  const { language } = useLanguage();
  const { isLoggedIn, showLoginForm } = useAppContext();
  const { setDoForgottenPassword } = useSessionContext();
  const { setIsOpen, setSelectedItem } = useMenuItemsContext();
  const fallbackImage = "/image/membership/star4.png";
  const [imgSrc, setImgSrc] = useState(
    offer.mainPicUrl?.includes("empty.gif")
      ? fallbackImage
      : offer.mainPicUrl || fallbackImage,
  );

  useEffect(() => {
    setImgSrc(
      offer.mainPicUrl?.includes("empty.gif")
        ? fallbackImage
        : offer.mainPicUrl || fallbackImage,
    );
  }, [offer.mainPicUrl]);
  const copy = {
    cs: {
      eyebrow: "softiq.cz",
      loggedOut: {
        title: "Aktivujte si členství",
        description:
          "Vytvořte si hlídacího psa na nemovitosti a najděte svůj vysněný domov nebo další investiční příležitost.",
        action: "Aktivovat členství",
      },
      loggedIn: {
        title: "Najděte svou vysněnou nemovitost",
        description:
          "Hlídací pes sleduje nové nabídky za vás, abyste svůj budoucí domov nebo investici objevili mezi prvními.",
        action: "softiq.cz",
      },
    },
    en: {
      eyebrow: "softiq.cz",
      loggedOut: {
        title: "Activate your membership",
        description:
          "Create a real estate watchdog and find your dream home or next investment opportunity.",
        action: "Activate membership",
      },
      loggedIn: {
        title: "Find your dream property",
        description:
          "The real estate watchdog monitors new listings so you can discover your future home or investment among the first.",
        action: "softiq.cz",
      },
    },
  }[language];
  const promotionalCopy = isLoggedIn ? copy.loggedIn : copy.loggedOut;

  const openMembershipDialog = () => {
    if (!isLoggedIn) {
      setDoForgottenPassword(false);
      showLoginForm(false);
      requestAnimationFrame(() => {
        const authPanel = document.getElementById("prihlaseni");
        authPanel?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        authPanel
          ?.querySelector<HTMLInputElement>('input[name="email"]')
          ?.focus({ preventScroll: true });
      });
      return;
    }

    setSelectedItem({
      name: "Změnit členství",
      href: "",
      icon: ArrowUpCircleIcon,
    });
    setIsOpen(true);
  };

  return (
    <div className="relative flex h-full min-h-[420px] w-full overflow-hidden rounded-3xl bg-slate-950 px-7 py-8 text-white sm:px-10 sm:py-10">
      <Image
        src={imgSrc}
        alt=""
        aria-hidden="true"
        fill
        loading="lazy"
        sizes="(min-width: 1024px) 50vw, 100vw"
        className="scale-105 object-cover blur-[2px]"
        onError={() => setImgSrc(fallbackImage)}
      />
      <div className="absolute inset-0 bg-softiq-primary/90" />
      <div className="absolute -right-20 -top-24 size-64 rounded-full bg-gradient-to-br from-black to-softiq-secondary blur-3xl" />
      <div className="absolute -bottom-28 -left-20 size-72 rounded-full bg-gradient-to-br from-black to-softiq-secondary blur-3xl" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/30" />

      <div className="relative z-10 flex w-full flex-col justify-between gap-10">
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-semibold uppercase tracking-[0.24em] text-white/70">
            {copy.eyebrow}
          </span>
          <ArrowUpCircleIcon className="size-8 text-white/70" aria-hidden />
        </div>

        <div className="max-w-lg">
          <h3 className="text-3xl font-bold leading-tight sm:text-4xl">
            {promotionalCopy.title}
          </h3>
          <p className="mt-4 text-base leading-relaxed text-white/80 sm:text-lg">
            {promotionalCopy.description}
          </p>
        </div>

        <Button
          type="button"
          size="lg"
          className="min-h-12 w-full bg-white text-slate-950 hover:bg-white/90 sm:w-fit"
          onClick={openMembershipDialog}
        >
          {promotionalCopy.action}
        </Button>
      </div>
    </div>
  );
}
